"use client"

import { useState, useEffect } from "react"
import { Container, Row, Col, Card, Button, Form, Alert, Badge, Spinner, Modal } from "react-bootstrap"
import { toast } from "react-hot-toast"
import { apiRequest, endpoints } from "../utils/api"
import MapComponent from "../components/MapComponent"

const CropRotationPage = () => {
  const [rotationPlans, setRotationPlans] = useState([])
  const [loading, setLoading] = useState(false)
  const [selectedLocation, setSelectedLocation] = useState(null)
  const [locationAnalysis, setLocationAnalysis] = useState(null)
  const [rotationYears, setRotationYears] = useState(4)
  const [selectedCrops, setSelectedCrops] = useState([])
  const [availableCrops, setAvailableCrops] = useState([])
  const [showPlanModal, setShowPlanModal] = useState(false)
  const [currentPlan, setCurrentPlan] = useState(null)

  useEffect(() => {
    fetchAvailableCrops()
  }, [])

  const fetchAvailableCrops = async () => {
    try {
      const response = await apiRequest(`${endpoints.crops}/all`)
      setAvailableCrops(response.crops || [])
    } catch (error) {
      console.error("Error fetching crops:", error)
      toast.error("Failed to load available crops")
    }
  }

  const handleLocationSelect = async (lat, lng) => {
    setLoading(true)
    setSelectedLocation({ lat, lng })

    try {
      // Fetch soil and climate analysis for the selected location
      const analysisResponse = await apiRequest(`${endpoints.cropRotation}/analyze?lat=${lat}&lng=${lng}`)

      setLocationAnalysis(analysisResponse.analysis)
      toast.success("Location analysis complete!")
    } catch (error) {
      console.error("Error analyzing location:", error)
      toast.error("Failed to analyze location conditions")
    } finally {
      setLoading(false)
    }
  }

  const generateRotationPlan = async () => {
    if (!selectedLocation || selectedCrops.length < 2) {
      toast.error("Please select a location and at least 2 crops")
      return
    }

    setLoading(true)
    try {
      const response = await apiRequest(`${endpoints.cropRotation}/generate`, {
        method: "POST",
        body: JSON.stringify({
          location: selectedLocation,
          crops: selectedCrops,
          years: rotationYears,
          preferences: {
            soilHealth: true,
            pestManagement: true,
            nutrientCycling: true,
          },
        }),
      })

      setRotationPlans([response.plan, ...rotationPlans])
      toast.success("Crop rotation plan generated successfully!")
    } catch (error) {
      console.error("Error generating rotation plan:", error)
      toast.error("Failed to generate rotation plan")
    } finally {
      setLoading(false)
    }
  }

  const viewPlanDetails = (plan) => {
    setCurrentPlan(plan)
    setShowPlanModal(true)
  }

  return (
    <Container fluid className="py-4">
      <Row>
        <Col lg={4}>
          <Card className="shadow-sm mb-4">
            <Card.Header className="bg-success text-white">
              <h5 className="mb-0">📍 Select Location</h5>
            </Card.Header>
            <Card.Body>
              <MapComponent onLocationSelect={handleLocationSelect} loading={loading} />
            </Card.Body>
          </Card>

          <Card className="shadow-sm mb-4">
            <Card.Header className="bg-success text-white">
              <h5 className="mb-0">🔄 Crop Rotation Planner</h5>
            </Card.Header>
            <Card.Body>
              <Form>
                <Form.Group className="mb-3">
                  <Form.Label>Location Coordinates</Form.Label>
                  <Form.Control
                    type="text"
                    value={
                      selectedLocation ? `${selectedLocation.lat.toFixed(4)}, ${selectedLocation.lng.toFixed(4)}` : ""
                    }
                    readOnly
                    disabled
                  />
                  {selectedLocation && <Form.Text className="text-success">✓ Location selected</Form.Text>}
                </Form.Group>

                {locationAnalysis && (
                  <div className="mb-3 p-3 bg-light rounded">
                    <h6 className="mb-2">🌍 Location Analysis</h6>
                    <div className="small">
                      <div className="mb-1">
                        <strong>Soil Type:</strong> {locationAnalysis.soilType}
                      </div>
                      <div className="mb-1">
                        <strong>Climate Zone:</strong> {locationAnalysis.climateZone}
                      </div>
                      <div className="mb-1">
                        <strong>Temperature Range:</strong> {locationAnalysis.temperatureRange}
                      </div>
                      <div className="mb-1">
                        <strong>Annual Rainfall:</strong> {locationAnalysis.annualRainfall}
                      </div>
                      <div>
                        <strong>pH Level:</strong> {locationAnalysis.phLevel}
                      </div>
                    </div>
                  </div>
                )}

                <Form.Group className="mb-3">
                  <Form.Label>Rotation Period (Years)</Form.Label>
                  <Form.Select
                    value={rotationYears}
                    onChange={(e) => setRotationYears(Number.parseInt(e.target.value))}
                  >
                    <option value={3}>3 Years</option>
                    <option value={4}>4 Years</option>
                    <option value={5}>5 Years</option>
                    <option value={6}>6 Years</option>
                  </Form.Select>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Select Crops for Rotation</Form.Label>
                  <div
                    style={{
                      maxHeight: "200px",
                      overflowY: "auto",
                      border: "1px solid #dee2e6",
                      borderRadius: "0.375rem",
                      padding: "0.5rem",
                    }}
                  >
                    {availableCrops.map((crop) => (
                      <Form.Check
                        key={crop.id}
                        type="checkbox"
                        id={`crop-${crop.id}`}
                        label={`${crop.name} (${crop.category})`}
                        checked={selectedCrops.some((c) => c.id === crop.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedCrops([...selectedCrops, crop])
                          } else {
                            setSelectedCrops(selectedCrops.filter((c) => c.id !== crop.id))
                          }
                        }}
                      />
                    ))}
                  </div>
                  <Form.Text className="text-muted">Select at least 2 crops for rotation planning</Form.Text>
                </Form.Group>

                <Button
                  variant="success"
                  className="w-100"
                  onClick={generateRotationPlan}
                  disabled={loading || !selectedLocation || selectedCrops.length < 2}
                >
                  {loading ? (
                    <>
                      <Spinner size="sm" className="me-2" />
                      Generating Plan...
                    </>
                  ) : (
                    "🔄 Generate Rotation Plan"
                  )}
                </Button>
              </Form>
            </Card.Body>
          </Card>

          {selectedCrops.length > 0 && (
            <Card className="shadow-sm">
              <Card.Header>
                <h6 className="mb-0">Selected Crops ({selectedCrops.length})</h6>
              </Card.Header>
              <Card.Body>
                <div className="d-flex flex-wrap gap-2">
                  {selectedCrops.map((crop) => (
                    <Badge key={crop.id} bg="success" className="p-2">
                      {crop.name}
                      <Button
                        variant="link"
                        size="sm"
                        className="text-white p-0 ms-2"
                        onClick={() => setSelectedCrops(selectedCrops.filter((c) => c.id !== crop.id))}
                      >
                        ×
                      </Button>
                    </Badge>
                  ))}
                </div>
              </Card.Body>
            </Card>
          )}
        </Col>

        <Col lg={8}>
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h4>🌾 Rotation Plans</h4>
            <Badge bg="info" className="p-2">
              {rotationPlans.length} Plans Generated
            </Badge>
          </div>

          {rotationPlans.length === 0 ? (
            <Alert variant="info" className="text-center py-5">
              <h5>🔄 No Rotation Plans Yet</h5>
              <p>Generate your first crop rotation plan using the form on the left.</p>
              <small className="text-muted">
                Crop rotation helps improve soil health, reduce pests, and maximize yields.
              </small>
            </Alert>
          ) : (
            <Row>
              {rotationPlans.map((plan, index) => (
                <Col md={6} key={index} className="mb-4">
                  <Card className="shadow-sm h-100">
                    <Card.Header className="bg-light">
                      <div className="d-flex justify-content-between align-items-center">
                        <h6 className="mb-0">Plan #{index + 1}</h6>
                        <Badge bg="success">{plan.years} Years</Badge>
                      </div>
                    </Card.Header>
                    <Card.Body>
                      <div className="mb-3">
                        <small className="text-muted">Location:</small>
                        <div>{plan.location.name}</div>
                      </div>

                      <div className="mb-3">
                        <small className="text-muted">Rotation Sequence:</small>
                        <div className="mt-1">
                          {plan.sequence.map((year, yearIndex) => (
                            <div key={yearIndex} className="d-flex align-items-center mb-1">
                              <Badge bg="outline-primary" className="me-2">
                                Y{yearIndex + 1}
                              </Badge>
                              <span className="small">{year.crop}</span>
                            </div>
                          ))}
                        </div>
                      </div>

                      <div className="mb-3">
                        <small className="text-muted">Benefits:</small>
                        <div className="d-flex flex-wrap gap-1 mt-1">
                          {plan.benefits.slice(0, 3).map((benefit, bIndex) => (
                            <Badge key={bIndex} bg="success" className="small">
                              {benefit}
                            </Badge>
                          ))}
                        </div>
                      </div>

                      <Button
                        variant="outline-success"
                        size="sm"
                        className="w-100"
                        onClick={() => viewPlanDetails(plan)}
                      >
                        View Full Plan
                      </Button>
                    </Card.Body>
                  </Card>
                </Col>
              ))}
            </Row>
          )}
        </Col>
      </Row>

      {/* Plan Details Modal */}
      <Modal show={showPlanModal} onHide={() => setShowPlanModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>🔄 Detailed Rotation Plan</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {currentPlan && (
            <div>
              <Row className="mb-4">
                <Col md={6}>
                  <h6>📍 Location Details</h6>
                  <p>{currentPlan.location.name}</p>
                  <p className="text-muted small">
                    Coordinates: {currentPlan.location.lat}, {currentPlan.location.lng}
                  </p>
                </Col>
                <Col md={6}>
                  <h6>⏱️ Plan Duration</h6>
                  <p>{currentPlan.years} Years Rotation Cycle</p>
                  <p className="text-muted small">Generated: {new Date(currentPlan.createdAt).toLocaleDateString()}</p>
                </Col>
              </Row>

              <h6>🌾 Year-by-Year Rotation</h6>
              <div className="mb-4">
                {currentPlan.sequence.map((year, index) => (
                  <Card key={index} className="mb-2">
                    <Card.Body className="py-2">
                      <Row className="align-items-center">
                        <Col md={2}>
                          <Badge bg="primary" className="p-2">
                            Year {index + 1}
                          </Badge>
                        </Col>
                        <Col md={4}>
                          <strong>{year.crop}</strong>
                        </Col>
                        <Col md={6}>
                          <small className="text-muted">{year.reason}</small>
                        </Col>
                      </Row>
                    </Card.Body>
                  </Card>
                ))}
              </div>

              <h6>✅ Expected Benefits</h6>
              <Row>
                {currentPlan.benefits.map((benefit, index) => (
                  <Col md={6} key={index} className="mb-2">
                    <div className="d-flex align-items-center">
                      <Badge bg="success" className="me-2">
                        ✓
                      </Badge>
                      <span className="small">{benefit}</span>
                    </div>
                  </Col>
                ))}
              </Row>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowPlanModal(false)}>
            Close
          </Button>
          <Button variant="success">📄 Export Plan</Button>
        </Modal.Footer>
      </Modal>
    </Container>
  )
}

export default CropRotationPage
